import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Search, User } from 'lucide-react';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const location = useLocation();
  
  // Determine if we're on a page with a dark hero
  const isDarkHeroPage = location.pathname === '/' || location.pathname.startsWith('/t/');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Split navigation - left and right of logo
  const leftNavItems = [
    { name: 'ABOUT', path: '/about' },
    { name: 'TALENT', path: '/talents' },
    { name: 'CASTINGS', path: '/castings' },
  ];

  const rightNavItems = [
    { name: 'NEWS', path: '/news' },
    { name: 'CONTACT', path: '/contact' },
  ];

  const allNavItems = [
    { name: 'HOME', path: '/' },
    ...leftNavItems,
    ...rightNavItems,
    { name: 'NEW FACES', path: '/new-faces' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const NavLink = ({ item }) => (
    <Link
      to={item.path}
      className={`text-[13px] font-medium tracking-[0.25em] transition-colors duration-300 whitespace-nowrap ${
        isActive(item.path)
          ? 'text-[#c9a94d]'
          : isDarkHeroPage && !isScrolled
            ? 'text-white/90 hover:text-white'
            : 'text-gray-800 hover:text-[#c9a94d]'
      }`}
    >
      {item.name}
    </Link>
  );

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white shadow-md'
          : isDarkHeroPage
            ? 'bg-transparent'
            : 'bg-[#f5f1eb]'
      }`}
    >
      {/* Main Header */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-[90px]">
          
          {/* Left Navigation - Desktop */}
          <div className="hidden lg:flex items-center flex-1">
            <nav className="flex items-center gap-12">
              {leftNavItems.map((item) => (
                <NavLink key={item.name} item={item} />
              ))}
            </nav>
          </div>

          {/* Center Logo */}
          <Link to="/" className="flex-shrink-0">
            <img
              src={isDarkHeroPage && !isScrolled 
                ? "/images/hutson-logo-white.png"
                : "/images/hutson-logo-dark.png"
              }
              alt="Hutson Talent Agency"
              className="h-[60px] w-auto transition-opacity duration-300"
            />
          </Link>

          {/* Right Navigation - Desktop */}
          <div className="hidden lg:flex items-center justify-end flex-1">
            <nav className="flex items-center gap-12">
              {rightNavItems.map((item) => (
                <NavLink key={item.name} item={item} />
              ))}
              
              {/* Icons + Join CTA with separator */}
              <div className={`flex items-center gap-5 ml-6 pl-6 border-l ${isDarkHeroPage && !isScrolled ? 'border-white/30' : 'border-gray-300'}`}>
                <button
                  onClick={() => setIsSearchOpen(!isSearchOpen)}
                  className={`transition-colors ${isDarkHeroPage && !isScrolled ? 'text-white/90 hover:text-white' : 'text-gray-700 hover:text-[#c9a94d]'}`}
                  aria-label="Search"
                >
                  <Search className="w-[18px] h-[18px]" strokeWidth={1.5} />
                </button>

                <Link
                  to="/login"
                  className={`text-[12px] font-medium tracking-[0.2em] transition-colors ${isDarkHeroPage && !isScrolled ? 'text-white/90 hover:text-white' : 'text-gray-700 hover:text-[#c9a94d]'}`}
                  aria-label="Login"
                >
                  LOGIN
                </Link>

                {/* Prominent JOIN FREE CTA — primary signup entry point for talent */}
                <Link
                  to="/register"
                  className="inline-flex items-center px-4 py-2 bg-[#c9a94d] hover:bg-[#b8993d] text-white text-[12px] font-semibold tracking-[0.2em] transition-colors whitespace-nowrap"
                  data-testid="header-join-cta"
                >
                  JOIN FREE
                </Link>
              </div>
            </nav>
          </div>

          {/* Mobile Menu Button & Icons */}
          <div className="flex lg:hidden items-center gap-4">
            <button
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              className={`transition-colors ${isDarkHeroPage && !isScrolled ? 'text-white/90 hover:text-white' : 'text-gray-700 hover:text-[#c9a94d]'}`}
            >
              <Search className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <Link
              to="/register"
              className="inline-flex items-center px-3 py-1.5 bg-[#c9a94d] hover:bg-[#b8993d] text-white text-[11px] font-semibold tracking-[0.15em] transition-colors whitespace-nowrap"
              data-testid="header-join-cta-mobile"
            >
              JOIN
            </Link>
            <Link
              to="/login"
              className={`transition-colors ${isDarkHeroPage && !isScrolled ? 'text-white/90 hover:text-white' : 'text-gray-700 hover:text-[#c9a94d]'}`}
              aria-label="Login"
            >
              <User className="w-5 h-5" strokeWidth={1.5} />
            </Link>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`transition-colors ${isDarkHeroPage && !isScrolled ? 'text-white/90 hover:text-white' : 'text-gray-700 hover:text-[#c9a94d]'}`}
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" strokeWidth={1.5} />
              ) : (
                <Menu className="w-6 h-6" strokeWidth={1.5} />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Search Bar - Slides down */}
      <div
        className={`overflow-hidden transition-all duration-300 bg-white border-t border-gray-100 ${
          isSearchOpen ? 'max-h-20 py-4' : 'max-h-0 py-0'
        }`}
      >
        <div className="max-w-2xl mx-auto px-6">
          <div className="relative">
            <input
              type="text"
              placeholder="Search talents, news, or opportunities..."
              className="w-full px-4 py-3 pl-12 bg-gray-50 border-0 focus:outline-none focus:ring-2 focus:ring-[#c9a94d]/50 transition-all text-sm"
            />
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <button
              onClick={() => setIsSearchOpen(false)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 bg-[#f5f1eb] ${
          isMobileMenuOpen ? 'max-h-[500px] border-t border-gray-200' : 'max-h-0'
        }`}
      >
        <nav className="px-6 py-8 space-y-1">
          {allNavItems.map((item) => (
            <Link
              key={item.name}
              to={item.path}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block py-3 text-base font-medium tracking-[0.15em] border-b border-gray-200/50 ${
                isActive(item.path)
                  ? 'text-[#c9a94d]'
                  : 'text-gray-800 hover:text-[#c9a94d]'
              }`}
            >
              {item.name}
            </Link>
          ))}
          <Link
            to="/register"
            onClick={() => setIsMobileMenuOpen(false)}
            className="block mt-4 py-3 text-center text-base font-semibold tracking-[0.15em] bg-[#c9a94d] hover:bg-[#b8993d] text-white transition-colors"
          >
            JOIN FREE
          </Link>
          <Link
            to="/login"
            onClick={() => setIsMobileMenuOpen(false)}
            className="block py-3 text-base font-medium tracking-[0.15em] text-gray-800 hover:text-[#c9a94d]"
          >
            LOGIN
          </Link>
        </nav>
      </div>
    </header>
  );
};

export default Header;
